// ReviewModal.jsx
import {useState, useEffect} from 'react';
import {useNotifyHook} from '../hooks/useNotify';
import {api} from '../../api';

const ReviewModal = ({
    isOpen,
    onClose,
    bookingId,
    busId,
    routeId,
    vendorId,
    bookingNumber,
    onReviewSubmitted,
    existingReview // नया प्रॉप जोड़ें
}) => {
    const [rating, setRating] = useState(existingReview?.rate || 5);
    const [reviewText, setReviewText] = useState(existingReview?.review || '');
    const [isEditMode, setIsEditMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const notify = useNotifyHook();

    const handleSubmit = async () => {
        if (!reviewText.trim()) {
            notify.error('Please write a review');
            return;
        }

        if (rating < 1 || rating > 5) {
            notify.error('Please select a valid rating');
            return;
        }

        try {
            setSubmitting(true);

            const response = await fetch(api.submitReview, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${
                        localStorage.getItem('token') || ''
                    }`
                },
                body: JSON.stringify(
                    {
                        booking_id: bookingId,
                        bus_id: busId,
                        route_id: routeId,
                        vendor_id: vendorId,
                        rating: rating,
                        review: reviewText
                    }
                )
            });

            const result = await response.json();

            if (result.status) {
                notify.success(existingReview ? 'Review updated successfully!' : 'Review submitted successfully!');
                onReviewSubmitted();
                onClose();
            } else {
                throw new Error(result.message || 'Failed to submit review');
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            notify.error(error.message || 'Failed to submit review');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (existingReview && isEditMode) {
            setRating(existingReview.rate);
            setReviewText(existingReview.review);
            setIsEditMode(false);
        } else {
            setRating(5);
            setReviewText('');
        }
        onClose();
    };

    if (!isOpen) 
        return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg max-w-md w-full">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {existingReview ? 'Your Review' : 'Write a Review'}
                    </h2>
                    <button onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 text-xl">
                        ✕
                    </button>
                </div>

                <div className="p-6">
                    {/* Booking Info */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-600">Booking ID:</span>
                            <span className="font-medium">#{bookingNumber}</span>
                        </div>
                    </div>

                    {existingReview && !isEditMode ? (
                        <div className="mb-6">
                            <div className="flex items-center mb-3">
                                <span className="text-lg font-medium mr-2">Your Rating:</span>
                                <div className="flex">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span key={star} className="text-2xl">
                                            <span className={star <= existingReview.rate ? 'text-yellow-400' : 'text-gray-300'}>
                                                ★
                                            </span>
                                        </span>
                                    ))}
                                </div>
                                <span className="ml-2 text-gray-600">({existingReview.rate}/5)</span>
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Your Review:</label>
                                <p className="text-gray-700 bg-gray-50 p-3 rounded border">
                                    {existingReview.review}
                                </p>
                            </div>

                          
                            
                            {/* अगर reply है तो दिखाएं */}
                            {existingReview.reply && (
                                <div className="mb-4 pt-4 border-t border-gray-200">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Operator's Reply:</label>
                                    <p className="text-gray-700 bg-green-50 p-3 rounded border border-green-200">
                                        {existingReview.reply}
                                    </p>
                                </div>
                            )}
                            
                            <div className="flex justify-end space-x-3">
                              
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Rating Section */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    {existingReview ? 'Edit Your Rating' : 'How would you rate your experience?'}
                                </label>
                                <div className="flex items-center space-x-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            className="text-2xl focus:outline-none"
                                        >
                                            <span className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}>
                                                ★
                                            </span>
                                        </button>
                                    ))}
                                    <span className="ml-2 text-gray-600">({rating}/5)</span>
                                </div>
                                <div className="flex justify-between mt-1 text-xs text-gray-500">
                                    <span>Poor</span>
                                    <span>Excellent</span>
                                </div>
                            </div>

                            {/* Review Text */}
                            <div className="mb-6">
                                <label htmlFor="review" className="block text-sm font-medium text-gray-700 mb-2">
                                    {existingReview ? 'Edit Your Review' : 'Share your experience'}
                                </label>
                                <textarea
                                    id="review"
                                    rows="4"
                                    value={reviewText}
                                    onChange={(e) => setReviewText(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={existingReview ? "Edit your review..." : "Tell us about your journey experience..."}
                                    maxLength="500"
                                />
                                <div className="text-right text-xs text-gray-500 mt-1">
                                    {reviewText.length}/500 characters
                                </div>
                            </div>

                            {/* Tips for Good Review */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                                <p className="text-xs text-blue-700">
                                    💡 <strong>Tips:</strong> Mention bus condition, driver behavior, punctuality, cleanliness, and overall experience.
                                </p>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end space-x-3">
                                {existingReview && isEditMode ? (
                                    <>
                                        <button
                                            onClick={() => {
                                                setIsEditMode(false);
                                                setRating(existingReview.rate);
                                                setReviewText(existingReview.review);
                                            }}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel Edit
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting || !reviewText.trim()}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {submitting ? 'Updating...' : 'Update Review'}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleClose}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting || !reviewText.trim()}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Review'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewModal;